/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.
*/
import { Save, Video } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'

import { safeJsonParse } from '../utils/json-parser'

export const VEO_RESOLUTION_PRICE_KEYS = [
  'veo-3-1',
  'veo-3-1-1080p',
  'veo-3-1-4k',
  'veo-omni-flash',
  'veo-omni-flash-1080p',
  'veo-omni-flash-4k',
  'veo-omni-flash-video-edit',
  'veo-omni-flash-video-edit-1080p',
  'veo-omni-flash-video-edit-4k',
] as const

export const VEO_RESOLUTION_PRICE_KEY_SET = new Set<string>(
  VEO_RESOLUTION_PRICE_KEYS
)

const VEO_PRICING_MODELS = [
  {
    name: 'VEO 3.1',
    model: 'veo-3-1',
    duration: '8 秒',
    fields: [
      { label: '720P', key: 'veo-3-1' },
      { label: '1080P', key: 'veo-3-1-1080p' },
      { label: '4K', key: 'veo-3-1-4k' },
    ],
  },
  {
    name: 'VEO Omni Flash',
    model: 'veo-omni-flash',
    duration: '10 秒',
    fields: [
      { label: '720P', key: 'veo-omni-flash' },
      { label: '1080P', key: 'veo-omni-flash-1080p' },
      { label: '4K', key: 'veo-omni-flash-4k' },
    ],
  },
  {
    name: 'VEO Omni Flash Video Edit',
    model: 'veo-omni-flash-video-edit',
    duration: '10 秒',
    fields: [
      { label: '720P', key: 'veo-omni-flash-video-edit' },
      { label: '1080P', key: 'veo-omni-flash-video-edit-1080p' },
      { label: '4K', key: 'veo-omni-flash-video-edit-4k' },
    ],
  },
] as const

type VEOResolutionPricingProps = {
  modelPrice: string
  onChange: (value: string) => void
  onSave: () => void | Promise<void>
  isSaving: boolean
}

function ResolutionPriceInput(props: {
  modelName: string
  resolution: string
  value?: number
  onCommit: (value: number) => void
}) {
  const [draft, setDraft] = useState(
    props.value === undefined ? '' : String(props.value)
  )

  useEffect(() => {
    setDraft(props.value === undefined ? '' : String(props.value))
  }, [props.value])

  const commit = useCallback((rawValue: string) => {
    const value = Number(rawValue)
    if (!rawValue.trim() || !Number.isFinite(value) || value < 0) return false
    props.onCommit(value)
    return true
  }, [props])

  return (
    <Input
      aria-label={`${props.modelName} ${props.resolution} 单次价格`}
      className='pr-7 tabular-nums'
      type='number'
      min='0'
      step='0.01'
      value={draft}
      onChange={(event) => {
        const next = event.target.value
        setDraft(next)
        commit(next)
      }}
      onBlur={() => {
        if (!commit(draft)) {
          setDraft(props.value === undefined ? '' : String(props.value))
        }
      }}
    />
  )
}

export function VEOResolutionPricing({
  modelPrice,
  onChange,
  onSave,
  isSaving,
}: VEOResolutionPricingProps) {
  const priceMap = useMemo(
    () => safeJsonParse<Record<string, number>>(modelPrice, {
      fallback: {},
      silent: true,
    }),
    [modelPrice]
  )

  const updatePrice = useCallback(
    (key: string, value: number) => {
      onChange(JSON.stringify({ ...priceMap, [key]: value }, null, 2))
    },
    [onChange, priceMap]
  )

  return (
    <Card>
      <CardHeader className='border-b'>
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <div className='space-y-1'>
            <CardTitle className='flex items-center gap-2'>
              <Video className='size-4' />
              VEO 分辨率定价
            </CardTitle>
            <CardDescription>
              用户调用基础模型并通过 resolution 选择清晰度。此处保存后会同步写入内部计费档位，不会新增可调用的后缀模型。
            </CardDescription>
          </div>
          <Button size='sm' onClick={onSave} disabled={isSaving}>
            <Save data-icon='inline-start' />
            {isSaving ? '保存中...' : '保存 VEO 价格'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className='pt-4'>
        <div className='grid gap-3 xl:grid-cols-3'>
          {VEO_PRICING_MODELS.map((item) => (
            <section key={item.model} className='rounded-lg border p-3'>
              <div className='mb-3 flex items-center justify-between gap-2'>
                <div className='font-medium'>{item.name}</div>
                <span className='text-muted-foreground text-xs'>固定 {item.duration}</span>
              </div>
              <div className='grid grid-cols-3 gap-2'>
                {item.fields.map((field) => (
                  <label key={field.key} className='min-w-0 space-y-1'>
                    <span className='text-muted-foreground text-xs'>{field.label}</span>
                    <div className='relative'>
                      <ResolutionPriceInput
                        modelName={item.name}
                        resolution={field.label}
                        value={priceMap[field.key]}
                        onCommit={(value) => updatePrice(field.key, value)}
                      />
                      <span className='text-muted-foreground pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 text-xs'>
                        ¥
                      </span>
                    </div>
                    <span className='text-muted-foreground block text-[11px]'>每次</span>
                  </label>
                ))}
              </div>
            </section>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
